import { NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { KfzDokTypMap } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/haftpflicht-kfz-schaden/haftpflicht-kfz-schaden.types';
import { DocumentSelectOption, GeschaedigtesFahrzeug, HaftpflichtSchadenKfz } from '@gsm/entities';
import { DynamicFormControlModel } from '@ng-dynamic-forms/core';
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { getDisplayName } from '../../helpers/display-name.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { DokumentenAngabeModel } from '../dokumenten-angabe/dokumenten-angabe.model';
import { GeschaedigtePersonDialogData, HaftpflichtGeschaedigtePersonDialogComponent } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person-dialog/haftpflicht-geschaedigte-person-dialog.component';
import { HaftpflichtGeschaedigtePersonModel } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { HAFTPFLICHT_KFZ_SCHADEN_TEMPLATE } from './haftpflicht-kfz-schaden.config';
import { HaftpflichtKfzSchadenModel } from './haftpflicht-kfz-schaden.model';

// NOTE:
// The base class CustomDynamicFormControlComponent already includes all inputs and outputs needed, as well
// as additional convenience methods onDisabledChange() and onValueChange, for when model.disabled and model.value change,
// respectively.

@Component({
  selector:    'gsm-haftpflicht-kfz-schaden',
  templateUrl: 'haftpflicht-kfz-schaden.component.html',
  styleUrls:   ['haftpflicht-kfz-schaden.component.scss'],
  standalone:  true,
  imports:     [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    NgIf,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMiniFabButton
  ]
})
export class HaftpflichtKfzSchadenComponent extends CustomDynamicFormControlComponent<HaftpflichtKfzSchadenModel> implements OnInit {

  private dialog = inject(MatDialog);
  private formService = inject(CustomDynamicFormService);

  private _dokTypMap: KfzDokTypMap = {} as KfzDokTypMap;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    if (!this.model.value) {
      this.model.value = [];
    }
    this._dokTypMap = keyBy<DocumentSelectOption>(this.model.additional?.docTypBelege || [], 'value') as KfzDokTypMap;
    this._emitChange();
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const isValid = this.model.value?.length;
    return isValid ? null : {kfzSchaden: true};
  }

  getName(schaden: HaftpflichtSchadenKfz): string {
    return schaden.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
      ? 'Wird nachgebracht'
      : schaden.geschaedigtePerson?.nachname
        ? getDisplayName([
          schaden.geschaedigtePerson.vorname,
          schaden.geschaedigtePerson.nachname
        ])
        : 'Unbekannt';
  }

  onAddClick() {
    this._editItem({}, null);
  }

  onEditClick(item: HaftpflichtSchadenKfz, index: number) {
    this._editItem(item, index, true);
  }

  onRemoveClick(index: number) {
    this.model.value = this.model.value.filter((_, i) => i !== index);
    this._emitChange();
  }

  private _editItem(item: HaftpflichtSchadenKfz, index: number | null, isEditEvent = false) {
    this.dialog
      .open<HaftpflichtGeschaedigtePersonDialogComponent, GeschaedigtePersonDialogData, AnyFormControlModel[]>(
        HaftpflichtGeschaedigtePersonDialogComponent,
        {
          // allow quick closing only in view mode
          disableClose: !this.model.disabled,
          minWidth:     '600px',
          data:         {
            formConfig:      HAFTPFLICHT_KFZ_SCHADEN_TEMPLATE(item, this.model.value, this.model.additional, this._dokTypMap, isEditEvent),
            heading:         'KFZ Schaden - Geschädigte Person',
            disabled:        this.model.disabled,
            parentComponent: this,
            onFormChange:    this._onFormChange
          }
        }
      )
      .afterClosed()
      .subscribe(result => {
        if (result) {
          const resultEntity = this._getValueFromForm(result);
          this._saveEditedItem(resultEntity, index);
        }
      });
  }

  private _saveEditedItem(item: HaftpflichtSchadenKfz, index: number | null) {
    // index == null means that a new item should be added
    if (index !== null) {
      this.model.value = this.model.value.map((val, i) => (index === i ? item : val));
    } else {
      this.model.value = [
        ...this.model.value,
        item
      ];
    }
    this._emitChange();
  }

  // Needs to be an arrow function to preserve "this" context.
  private _onFormChange = (models: DynamicFormControlModel[], changeModel?: AnyFormControlModel) => {
    if (changeModel?.id === 'geschaedigtePerson') {
      const geschaedigtePerson = (changeModel as HaftpflichtGeschaedigtePersonModel).value?.geschaedigtePerson;

      // If geschaedigtePerson has a nachname, use it as a Zahlungsempfaenger.
      if (geschaedigtePerson?.nachname) {
        const {
          vorname,
          nachname
        } = geschaedigtePerson;

        // Only add to kunde list if someone with the same name isn't there yet.
        if (!this.model.additional.kundeList?.some(kunde => kunde.vorname === vorname && kunde.nachname === nachname)) {
          const newKundeList = [
            ...(this.model.additional.kundeList || []),
            {
              vorname,
              nachname
            }
          ];

          // Set kunden property on DokumentenAngabe component.
          const belegeDokumente = this.formService.findModelById<DokumentenAngabeModel>('aufstellungBelege', models);
          belegeDokumente.additional.kunden = newKundeList;
        }
      }
    } else if (changeModel?.id === 'schadenhoeheUeber1000') {
      const ueber1000: boolean = !!changeModel.value;
      const belegeDokumente = this.formService.findModelById<DokumentenAngabeModel>('aufstellungBelege', models);
      const existingKostenVoranschlag = belegeDokumente.value.find(doc => doc.typ.value === 'KOSTEN' && doc.required);
      const newKostenVoranschlag = {
        typ:         this._dokTypMap.KOSTEN,
        bezeichnung: this._dokTypMap.KOSTEN?.label,
        required:    true,
        vorhanden:   'V'
      };

      if (!ueber1000 && existingKostenVoranschlag) {
        // if schadenhoehe unter 1000 and there is an existing kostenVoranschlag:
        if (isEqual(existingKostenVoranschlag, newKostenVoranschlag)) {
          // if existing kostenVoranschlag is unmodified from its initial state, delete it
          belegeDokumente.value = belegeDokumente.value.filter(doc => doc !== existingKostenVoranschlag);
        } else {
          // otherwise just change the required flag
          existingKostenVoranschlag.required = false;
          belegeDokumente.value = belegeDokumente.value;
        }
      } else if (ueber1000) {
        // if schadenhoehe over 1000, add kostenvoranschlag
        belegeDokumente.value = [
          {
            ...existingKostenVoranschlag,
            ...newKostenVoranschlag
          },
          ...belegeDokumente.value.filter(doc => doc !== existingKostenVoranschlag)
        ];
      }
    }
  };

  private _getValueFromForm(formModel: AnyFormControlModel[]): HaftpflichtSchadenKfz {
    const {
      geschaedigtePerson:      {
                                 id,
                                 vorhanden,
                                 geschaedigtePerson: person
                               } = {} as any,
      schadenhoehe_group:      {schadenhoeheUeber1000} = {} as any,
      kennzeichen_group:       {
                                 kennzeichen,
                                 kennzeichenUnbekanntBegruendung
                               } = {} as any,
      aufstellungBelege_group: {aufstellungBelege: belege} = {} as any
    } = getGroupValue(formModel);

    const geschaedigtePerson = {
      ...person,
      geschaedigtesFahrzeugList: [
        filterEmptyProps<GeschaedigtesFahrzeug>({
          kennzeichen,
          kennzeichenUnbekanntBegruendung
        })
      ]
    };

    const rawObject = () => {
      return vorhanden !== 'N'
        ? {
          id,
          vorhanden,
          geschaedigtePerson,
          belege,
          schadenhoeheUeber1000
        }
        : {
          id,
          vorhanden,
          belege
        };
    };

    return filterEmptyProps<HaftpflichtSchadenKfz>(rawObject());
  }
}
